import { CloudDeck, ConjugationTable, DeckWord, Translations } from '@lingoping/core';
import { StorageManager, isDevMockUserId } from '../storage/index.js';

/**
 * Decks live in the cloud (Docs/plans/CLOUD_DECKS_PLAN.md); this is the extension's one door to them.
 *
 * - Everything is read from a local copy first, so the Decks tab opens instantly and works offline
 *   after the first load; `refresh*` then updates that copy in the background.
 * - Every change (create / rename / delete a personal deck, add or remove words, follow, "Not now")
 *   is applied to the local copy at once and put in an ordered queue that is uploaded as soon as
 *   the server can be reached. The queue keeps the order things happened in, and word positions are
 *   decided when the words are added, so nothing is reordered by being uploaded later.
 * - The server side is a `DeckBackend`: Supabase with the signed-in session (see
 *   cloudDecksBackend.ts), or a local stand-in for the dev mode without a server (devCloudDecks.ts).
 */

/** The fixed id of the public deck new users start with (seeded by scripts/seed-basic-german.mjs). */
export const BASIC_GERMAN_DECK_ID = '00000000-0000-4000-8000-00000000de01';

/** Rows per request when reading a deck's words. */
export const WORDS_PAGE_SIZE = 1000;
/** Rows per request when writing words. */
export const WORDS_INSERT_CHUNK = 500;

export const MAX_DECK_WORDS = 20000;
export const MAX_PERSONAL_DECKS = 200;
export const MAX_LEMMA_LENGTH = 80;
export const MAX_CONTEXT_LENGTH = 300;
export const MAX_HINT_LENGTH = 200;
export const MAX_TITLE_LENGTH = 120;

/** The shared library row (`word_card_cache`) of a prepared word, as far as decks show it. */
export interface LibraryWord {
  id: string;
  lemma: string;
  translation: string | null;
  part_of_speech: string | null;
  phonetic_ipa: string | null;
  linguistic_meta: {
    article?: string | null;
    gender?: 'masculine' | 'feminine' | 'neuter' | null;
    plural?: string | null;
    plural_ipa?: string | null;
    /** Principal parts of a verb (what generate-card stores). */
    verb?: {
      auxiliary: 'haben' | 'sein';
      participle: string;
      present: string[];
      preterite: string[];
      konjunktiv2_ich: string;
      imperative_du: string;
      separable_prefix: string;
    } | null;
  } | null;
  image_url: string | null;
  example_target: string | null;
  example_native: string | null;
  translations: Translations | null;
  /** Every form that counts as known (only the local dev stand-in provides it). */
  forms?: string[];
  /** Ready-made tense tables (only the local dev stand-in provides them). */
  conjugation?: ConjugationTable;
  /** Which meaning of the word this library entry is, and its short German hint. */
  sense_key?: string;
  sense_hint?: string | null;
}

/** A deck word with its library row embedded when the word has been prepared. */
export interface CloudDeckWord extends DeckWord {
  word?: LibraryWord | null;
}

/** A word to add to a deck, in the order it should appear. */
export interface NewDeckWord {
  lemma: string;
  context?: string | null;
  hint?: string | null;
  /** One meaning of a word with several (an import makes one entry per meaning). */
  sense_key?: string;
  sense_label?: string | null;
  sense_hint?: string | null;
}

export interface DeckLists {
  /** The learner's own decks and the published public decks. */
  decks: CloudDeck[];
  /** The deck the learner follows, if any. */
  followingId: string | null;
  /** "Not now": deck word id -> ISO time. */
  deferrals: Record<string, string>;
  /** When the lists were last read from the server (null = never). */
  fetchedAt: string | null;
}

export interface OpenDeck {
  deck: CloudDeck;
  words: CloudDeckWord[];
  fetchedAt: string | null;
}

/** A word row as it is written to `deck_words`. */
export interface DeckWordRow {
  id: string;
  position: number;
  lemma: string;
  context: string | null;
  hint: string | null;
  sense_key: string;
  sense_label: string | null;
  sense_hint: string | null;
}

/** What makes two deck entries the same: the word and which meaning of it. */
export function deckEntryKey(word: { lemma: string; sense_key?: string | null }): string {
  return `${word.lemma.trim().toLowerCase()}\u0000${word.sense_key ?? ''}`;
}

/** One change waiting to be uploaded. */
export type DeckOp =
  | { type: 'createDeck'; deck: CloudDeck }
  | { type: 'renameDeck'; deckId: string; title: string }
  | { type: 'deleteDeck'; deckId: string }
  | { type: 'addWords'; deckId: string; words: DeckWordRow[] }
  | { type: 'removeWord'; deckId: string; wordId: string }
  | { type: 'follow'; deckId: string | null }
  | { type: 'defer'; deckWordId: string; at: string }
  | { type: 'undefer'; deckWordId: string };

interface QueuedOp {
  qid: string;
  op: DeckOp;
}

/** A server call failed. `retryable` = try again later (offline, signed out, server trouble). */
export class DeckBackendError extends Error {
  constructor(
    message: string,
    public readonly retryable: boolean
  ) {
    super(message);
  }
}

export interface DeckBackend {
  listDecks(): Promise<CloudDeck[]>;
  getFollowing(): Promise<string | null>;
  listDeferrals(): Promise<Record<string, string>>;
  /** All words of a deck in order (`position`, then `id`), library rows embedded. */
  listWords(deckId: string): Promise<CloudDeckWord[]>;
  /** Writes one change; must be safe to repeat. */
  apply(op: DeckOp, userId: string): Promise<void>;
}

export interface CloudDecksApi {
  readonly mode: 'cloud' | 'dev';
  /** The local copy of the lists (never touches the network). */
  readLists(): Promise<DeckLists>;
  /** Uploads waiting changes, then reads the lists from the server. `online: false` = local copy. */
  refreshLists(): Promise<{ lists: DeckLists; online: boolean }>;
  /** A deck and its words from the local copy (null when never loaded). */
  readDeck(deckId: string): Promise<OpenDeck | null>;
  /** Reads a deck's words from the server unless the local copy is current (`force` always reads). */
  refreshDeck(deckId: string, options?: { force?: boolean }): Promise<OpenDeck | null>;
  /** Refreshes the words of several decks one after another; `onDeck` fires as each one lands. */
  syncWords(deckIds: string[], onDeck?: (deckId: string) => void): Promise<void>;
  createDeck(title: string, words?: NewDeckWord[]): Promise<CloudDeck>;
  renameDeck(deckId: string, title: string): Promise<void>;
  deleteDeck(deckId: string): Promise<void>;
  /** Appends words in the given order, skipping words the deck already has. */
  addWords(deckId: string, words: NewDeckWord[]): Promise<{ added: number; skipped: number }>;
  removeWord(deckId: string, wordId: string): Promise<void>;
  /** Follows a deck (and stops following any other); null follows nothing. */
  follow(deckId: string | null): Promise<void>;
  deferWord(deckWordId: string): Promise<void>;
  undeferWord(deckWordId: string): Promise<void>;
  /** Uploads waiting changes now. Resolves true when nothing is left waiting. */
  flush(): Promise<boolean>;
  /** Messages about changes the server refused for good (limits...), once each. */
  takeFailures(): Promise<string[]>;
  pendingCount(): Promise<number>;
  /** False while the deck's creation is still waiting to be uploaded. */
  isUploaded(deckId: string): Promise<boolean>;
}

// ---------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------

function newId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

function clean(text: string | null | undefined, max: number): string | null {
  const value = (text ?? '').replace(/\s+/g, ' ').trim();
  return value ? value.slice(0, max) : null;
}

/**
 * Words ready to store: trimmed, empty and over-long entries dropped, repeats (case-insensitive,
 * also against `existing`) skipped, order kept. `skipped` counts what was left out.
 */
export function prepareDeckWords(
  words: NewDeckWord[],
  existing: Iterable<string> = [],
  room: number = MAX_DECK_WORDS
): { words: NewDeckWord[]; skipped: number } {
  // `existing`: the entries' keys (deckEntryKey) or plain words (= their first meaning).
  const seen = new Set<string>();
  for (const entry of existing)
    seen.add(entry.includes('\u0000') ? entry : deckEntryKey({ lemma: entry }));
  const result: NewDeckWord[] = [];
  let skipped = 0;
  for (const word of words) {
    const lemma = (word.lemma ?? '').replace(/\s+/g, ' ').trim();
    const senseKey = /^[a-z0-9_]{0,40}$/.test(word.sense_key ?? '') ? (word.sense_key ?? '') : '';
    const key = deckEntryKey({ lemma, sense_key: senseKey });
    if (!lemma || lemma.length > MAX_LEMMA_LENGTH || seen.has(key) || result.length >= room) {
      skipped++;
      continue;
    }
    seen.add(key);
    result.push({
      lemma,
      context: clean(word.context, MAX_CONTEXT_LENGTH),
      hint: clean(word.hint, MAX_HINT_LENGTH),
      sense_key: senseKey,
      sense_label: senseKey || word.sense_hint ? clean(word.sense_label, 60) : null,
      sense_hint: clean(word.sense_hint, 40),
    });
  }
  return { words: result, skipped };
}

function sortWords(words: CloudDeckWord[]): CloudDeckWord[] {
  return [...words].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

interface DeckWordsCache {
  words: CloudDeckWord[];
  fetchedAt: string;
  /** The deck's `updated_at` when the words were read: equal means nothing changed since. */
  deckUpdatedAt: string;
}

const EMPTY_LISTS: DeckLists = { decks: [], followingId: null, deferrals: {}, fetchedAt: null };

export interface CloudDecksDeps {
  userId: string;
  backend: DeckBackend;
  mode: 'cloud' | 'dev';
  now?: () => Date;
}

/**
 * The deck service on top of a backend. One instance per signed-in user; all state is in local
 * storage (per user), so a new instance picks up the same cache and queue.
 */
export function createCloudDecks(deps: CloudDecksDeps): CloudDecksApi {
  const { userId, backend, mode } = deps;
  const now = () => (deps.now ?? (() => new Date()))().toISOString();

  const listsKey = `lingoping_cloud_decks_v1_${userId}`;
  const wordsKey = (deckId: string) => `lingoping_cloud_deck_words_v1_${userId}_${deckId}`;
  const queueKey = `lingoping_cloud_deck_queue_v1_${userId}`;
  const knownDeckIdsKey = `lingoping_cloud_deck_words_index_v1_${userId}`;

  // Local changes are applied one at a time. `version` counts them, so a refresh that was
  // fetched before a change never overwrites it.
  let chain: Promise<unknown> = Promise.resolve();
  let version = 0;
  function locked<T>(fn: () => Promise<T>): Promise<T> {
    const run = chain.then(fn, fn);
    chain = run.catch(() => undefined);
    return run;
  }

  const readLocal = async (): Promise<DeckLists> =>
    (await StorageManager.getLocalValue<DeckLists>(listsKey)) ?? { ...EMPTY_LISTS };
  const writeLists = (lists: DeckLists) => StorageManager.setLocalValue(listsKey, lists);
  const readQueue = async (): Promise<QueuedOp[]> =>
    (await StorageManager.getLocalValue<QueuedOp[]>(queueKey)) ?? [];
  const writeQueue = (queue: QueuedOp[]) => StorageManager.setLocalValue(queueKey, queue);
  const readWordsCache = (deckId: string) =>
    StorageManager.getLocalValue<DeckWordsCache>(wordsKey(deckId));
  async function writeWordsCache(deckId: string, cache: DeckWordsCache | null) {
    await StorageManager.setLocalValue(wordsKey(deckId), cache);
    const ids = new Set((await StorageManager.getLocalValue<string[]>(knownDeckIdsKey)) ?? []);
    if (cache) ids.add(deckId);
    else ids.delete(deckId);
    await StorageManager.setLocalValue(knownDeckIdsKey, [...ids]);
  }

  function touchesDeck(op: DeckOp, deckId: string): boolean {
    switch (op.type) {
      case 'createDeck':
        return op.deck.id === deckId;
      case 'renameDeck':
      case 'deleteDeck':
      case 'addWords':
      case 'removeWord':
        return op.deckId === deckId;
      default:
        return false;
    }
  }

  async function enqueue(op: DeckOp, compact?: (queued: QueuedOp) => boolean): Promise<void> {
    let queue = await readQueue();
    if (compact) queue = queue.filter((q) => !compact(q));
    queue.push({ qid: newId(), op });
    await writeQueue(queue);
  }

  /** The local copy of a deck row, updated in the lists. */
  async function updateDeckRow(
    deckId: string,
    change: (deck: CloudDeck) => CloudDeck
  ): Promise<CloudDeck | null> {
    const lists = await readLocal();
    let updated: CloudDeck | null = null;
    lists.decks = lists.decks.map((d) => {
      if (d.id !== deckId) return d;
      updated = change(d);
      return updated;
    });
    await writeLists(lists);
    return updated;
  }

  // -- uploading --------------------------------------------------------------------------------

  let flushing: Promise<boolean> | null = null;
  let flushAgain = false;

  const failuresKey = `lingoping_cloud_deck_failures_v1_${userId}`;

  /** A sentence for the learner about a change the server refused for good. */
  function failureMessage(op: DeckOp, error: unknown): string {
    const text = (error as Error)?.message ?? '';
    if (/too_many_decks/.test(text)) {
      const title = op.type === 'createDeck' ? ` The deck “${op.deck.title}” was not saved.` : '';
      return `You have reached the limit of ${MAX_PERSONAL_DECKS} decks of your own.${title}`;
    }
    if (/deck_too_large/.test(text)) {
      return `A deck can hold up to ${MAX_DECK_WORDS.toLocaleString('en-US')} words. Some of the words you added were not saved.`;
    }
    return 'A change to your decks could not be saved.';
  }

  async function recordFailure(message: string): Promise<void> {
    const failures = (await StorageManager.getLocalValue<string[]>(failuresKey)) ?? [];
    await StorageManager.setLocalValue(failuresKey, [...failures, message]);
  }

  /** A change the server refused for good: undo its local effect so the screen matches the server. */
  async function discard(failed: QueuedOp, error: unknown): Promise<void> {
    const op = failed.op;
    await recordFailure(failureMessage(op, error));
    if (op.type === 'follow') {
      // A deck that cannot be followed (unpublished, someone else's): follow nothing.
      const lists = await readLocal();
      if (lists.followingId === op.deckId) {
        lists.followingId = null;
        await writeLists(lists);
      }
      return;
    }
    if (op.type === 'addWords') {
      // The server does not have these words: read the deck again on the next refresh.
      await writeWordsCache(op.deckId, null);
      return;
    }
    if (op.type !== 'createDeck') return;
    // The deck was never created: drop it and everything that was queued for it.
    const queue = (await readQueue())
      .filter((q) => q.qid !== failed.qid && !touchesDeck(q.op, op.deck.id))
      .map((q): QueuedOp =>
        q.op.type === 'follow' && q.op.deckId === op.deck.id
          ? { ...q, op: { type: 'follow', deckId: null } }
          : q
      );
    await writeQueue(queue);
    const lists = await readLocal();
    lists.decks = lists.decks.filter((d) => d.id !== op.deck.id);
    if (lists.followingId === op.deck.id) lists.followingId = null;
    await writeLists(lists);
    await writeWordsCache(op.deck.id, null);
  }

  async function runFlush(): Promise<boolean> {
    for (;;) {
      const next = await locked(async () => (await readQueue())[0] ?? null);
      if (!next) return true;
      try {
        await backend.apply(next.op, userId);
      } catch (error) {
        const retryable = !(error instanceof DeckBackendError) || error.retryable;
        if (retryable) return false;
        console.warn('[LingoPing] A deck change was refused and dropped:', next.op.type, error);
        await locked(() => discard(next, error));
      }
      await locked(async () => {
        await writeQueue((await readQueue()).filter((q) => q.qid !== next.qid));
      });
    }
  }

  function flush(): Promise<boolean> {
    if (flushing) {
      flushAgain = true;
      return flushing;
    }
    flushing = (async () => {
      try {
        let done = await runFlush();
        while (flushAgain) {
          flushAgain = false;
          done = await runFlush();
        }
        return done;
      } catch (error) {
        console.warn('[LingoPing] Uploading deck changes failed:', error);
        return false;
      } finally {
        flushing = null;
      }
    })();
    return flushing;
  }

  /** Changes are saved locally first; uploading never makes the caller wait. */
  const uploadSoon = () => void flush();

  // -- reading ----------------------------------------------------------------------------------

  async function refreshLists() {
    const startVersion = version;
    const queueEmpty = await flush();
    if (!queueEmpty) return { lists: await readLocal(), online: false };
    try {
      const [decks, followingId, deferrals] = await Promise.all([
        backend.listDecks(),
        backend.getFollowing(),
        backend.listDeferrals(),
      ]);
      const lists = await locked(async () => {
        // Changed while the server was being read: keep the local copy, the next refresh wins.
        if (version !== startVersion || (await readQueue()).length > 0) return readLocal();
        // A followed public deck can be unpublished later: then following it means following nothing.
        const visible = followingId && decks.some((d) => d.id === followingId);
        const fresh: DeckLists = {
          decks,
          followingId: visible ? followingId : null,
          deferrals,
          fetchedAt: now(),
        };
        await writeLists(fresh);
        return fresh;
      });
      return { lists, online: true };
    } catch (error) {
      console.warn('[LingoPing] Could not refresh decks:', (error as Error).message);
      return { lists: await readLocal(), online: false };
    }
  }

  async function readDeck(deckId: string): Promise<OpenDeck | null> {
    const lists = await readLocal();
    const deck = lists.decks.find((d) => d.id === deckId);
    if (!deck) return null;
    const cache = await readWordsCache(deckId);
    return { deck, words: cache?.words ?? [], fetchedAt: cache?.fetchedAt ?? null };
  }

  async function refreshDeck(
    deckId: string,
    options: { force?: boolean } = {}
  ): Promise<OpenDeck | null> {
    const current = await readDeck(deckId);
    if (!current) return null;
    const cache = await readWordsCache(deckId);
    if (!options.force && cache && cache.deckUpdatedAt === current.deck.updated_at) return current;
    // A deck whose creation has not been uploaded yet does not exist on the server.
    const queue = await readQueue();
    if (queue.some((q) => touchesDeck(q.op, deckId))) return current;
    const startVersion = version;
    try {
      const words = sortWords(await backend.listWords(deckId));
      return await locked(async () => {
        if (
          version !== startVersion ||
          (await readQueue()).some((q) => touchesDeck(q.op, deckId))
        ) {
          return readDeck(deckId);
        }
        const latest = await readDeck(deckId);
        if (!latest) return null;
        const fetchedAt = now();
        await writeWordsCache(deckId, { words, fetchedAt, deckUpdatedAt: latest.deck.updated_at });
        return { deck: latest.deck, words, fetchedAt };
      });
    } catch (error) {
      console.warn('[LingoPing] Could not read the deck:', (error as Error).message);
      return current;
    }
  }

  // -- changing ---------------------------------------------------------------------------------

  async function createDeck(title: string, words: NewDeckWord[] = []): Promise<CloudDeck> {
    const name = clean(title, MAX_TITLE_LENGTH);
    if (!name) throw new Error('A deck needs a name.');
    return locked(async () => {
      if ((await readLocal()).decks.filter((d) => !!d.owner_id).length >= MAX_PERSONAL_DECKS) {
        throw new Error(
          `You have reached the limit of ${MAX_PERSONAL_DECKS} decks of your own. Delete one to make a new one.`
        );
      }
      version++;
      const prepared = prepareDeckWords(words).words;
      const stamp = now();
      const deck: CloudDeck = {
        id: newId(),
        owner_id: userId,
        language: 'de',
        title: name,
        description: '',
        group_name: null,
        sort_order: 0,
        status: 'published',
        word_count: prepared.length,
        created_at: stamp,
        updated_at: stamp,
      };
      const rows: DeckWordRow[] = prepared.map((w, i) => ({
        id: newId(),
        position: i + 1,
        lemma: w.lemma,
        context: w.context ?? null,
        hint: w.hint ?? null,
        sense_key: w.sense_key ?? '',
        sense_label: w.sense_label ?? null,
        sense_hint: w.sense_hint ?? null,
      }));
      const lists = await readLocal();
      lists.decks = [...lists.decks, deck];
      await writeLists(lists);
      await writeWordsCache(deck.id, {
        words: rows.map((r) => ({ ...r, deck_id: deck.id, word_id: null, word: null })),
        fetchedAt: stamp,
        deckUpdatedAt: stamp,
      });
      await enqueue({ type: 'createDeck', deck });
      if (rows.length > 0) await enqueue({ type: 'addWords', deckId: deck.id, words: rows });
      uploadSoon();
      return deck;
    });
  }

  async function renameDeck(deckId: string, title: string): Promise<void> {
    const name = clean(title, MAX_TITLE_LENGTH);
    if (!name) throw new Error('A deck needs a name.');
    await locked(async () => {
      version++;
      if (!(await updateDeckRow(deckId, (d) => ({ ...d, title: name })))) return;
      await enqueue({ type: 'renameDeck', deckId, title: name });
      uploadSoon();
    });
  }

  async function deleteDeck(deckId: string): Promise<void> {
    await locked(async () => {
      version++;
      const lists = await readLocal();
      lists.decks = lists.decks.filter((d) => d.id !== deckId);
      if (lists.followingId === deckId) lists.followingId = null;
      await writeLists(lists);
      await writeWordsCache(deckId, null);
      const queue = await readQueue();
      const neverUploaded = queue.some(
        (q) => q.op.type === 'createDeck' && q.op.deck.id === deckId
      );
      // A deck that was never uploaded just disappears; otherwise the server is told.
      await writeQueue(
        queue
          .filter((q) => !touchesDeck(q.op, deckId))
          .map((q): QueuedOp =>
            q.op.type === 'follow' && q.op.deckId === deckId
              ? { ...q, op: { type: 'follow', deckId: null } }
              : q
          )
      );
      if (!neverUploaded) await enqueue({ type: 'deleteDeck', deckId });
      uploadSoon();
    });
    // Cards keep their progress; only the "chosen in this deck" label goes. The server takes the
    // label off itself when the deck is deleted there; for a word that still waits in "Up next"
    // it is taken off here at once, so the list never names a deck that is gone.
    for (const card of await StorageManager.getCards()) {
      if (card.source_deck_id === deckId && card.fsrs_state === 'New') {
        await StorageManager.setCardQueue(card.id, { source_deck_id: null });
      }
    }
  }

  async function addWords(deckId: string, words: NewDeckWord[]) {
    // A deck opened for the first time has no local words yet: read them so duplicates are known.
    if (!(await readWordsCache(deckId))) await refreshDeck(deckId, { force: true });
    return locked(async () => {
      version++;
      const deck = (await readLocal()).decks.find((d) => d.id === deckId);
      if (!deck) throw new Error('That deck no longer exists.');
      const cache = await readWordsCache(deckId);
      const existing = cache?.words ?? [];
      const { words: fresh, skipped } = prepareDeckWords(
        words,
        existing.map((w) => deckEntryKey(w)),
        MAX_DECK_WORDS - Math.max(existing.length, deck.word_count)
      );
      if (fresh.length === 0) return { added: 0, skipped };
      const start = Math.max(0, deck.word_count, ...existing.map((w) => w.position)) + 1;
      const rows: DeckWordRow[] = fresh.map((w, i) => ({
        id: newId(),
        position: start + i,
        lemma: w.lemma,
        context: w.context ?? null,
        hint: w.hint ?? null,
        sense_key: w.sense_key ?? '',
        sense_label: w.sense_label ?? null,
        sense_hint: w.sense_hint ?? null,
      }));
      const stamp = now();
      if (cache) {
        await writeWordsCache(deckId, {
          ...cache,
          words: [
            ...cache.words,
            ...rows.map((r) => ({ ...r, deck_id: deckId, word_id: null, word: null })),
          ],
        });
      }
      await updateDeckRow(deckId, (d) => ({
        ...d,
        word_count: d.word_count + rows.length,
        updated_at: stamp,
      }));
      await enqueue({ type: 'addWords', deckId, words: rows });
      uploadSoon();
      return { added: rows.length, skipped };
    });
  }

  async function removeWord(deckId: string, wordId: string): Promise<void> {
    await locked(async () => {
      version++;
      const cache = await readWordsCache(deckId);
      const had = cache?.words.some((w) => w.id === wordId) ?? true;
      if (cache) {
        await writeWordsCache(deckId, {
          ...cache,
          words: cache.words.filter((w) => w.id !== wordId),
        });
      }
      if (had) {
        await updateDeckRow(deckId, (d) => ({
          ...d,
          word_count: Math.max(0, d.word_count - 1),
          updated_at: now(),
        }));
      }
      const lists = await readLocal();
      if (lists.deferrals[wordId]) {
        delete lists.deferrals[wordId];
        await writeLists(lists);
      }
      await enqueue({ type: 'removeWord', deckId, wordId });
      uploadSoon();
    });
  }

  async function follow(deckId: string | null): Promise<void> {
    await locked(async () => {
      version++;
      const lists = await readLocal();
      lists.followingId = deckId;
      await writeLists(lists);
      // Only the last choice matters.
      await enqueue({ type: 'follow', deckId }, (q) => q.op.type === 'follow');
      uploadSoon();
    });
  }

  async function deferWord(deckWordId: string): Promise<void> {
    await locked(async () => {
      version++;
      const at = now();
      const lists = await readLocal();
      lists.deferrals = { ...lists.deferrals, [deckWordId]: at };
      await writeLists(lists);
      await enqueue(
        { type: 'defer', deckWordId, at },
        (q) => (q.op.type === 'defer' || q.op.type === 'undefer') && q.op.deckWordId === deckWordId
      );
      uploadSoon();
    });
  }

  async function undeferWord(deckWordId: string): Promise<void> {
    await locked(async () => {
      version++;
      const lists = await readLocal();
      const { [deckWordId]: _removed, ...rest } = lists.deferrals;
      lists.deferrals = rest;
      await writeLists(lists);
      await enqueue(
        { type: 'undefer', deckWordId },
        (q) => (q.op.type === 'defer' || q.op.type === 'undefer') && q.op.deckWordId === deckWordId
      );
      uploadSoon();
    });
  }

  return {
    mode,
    readLists: readLocal,
    refreshLists,
    readDeck,
    refreshDeck,
    async syncWords(deckIds, onDeck) {
      for (const id of deckIds) {
        const before = await readWordsCache(id);
        const result = await refreshDeck(id);
        if (result && (result.fetchedAt !== (before?.fetchedAt ?? null) || !before)) onDeck?.(id);
      }
    },
    createDeck,
    renameDeck,
    deleteDeck,
    addWords,
    removeWord,
    follow,
    deferWord,
    undeferWord,
    flush,
    async takeFailures() {
      const failures = (await StorageManager.getLocalValue<string[]>(failuresKey)) ?? [];
      if (failures.length > 0) await StorageManager.setLocalValue(failuresKey, []);
      return failures;
    },
    async pendingCount() {
      return (await readQueue()).length;
    },
    async isUploaded(deckId) {
      return !(await readQueue()).some(
        (q) => q.op.type === 'createDeck' && q.op.deck.id === deckId
      );
    },
  };
}

// ---------------------------------------------------------------------------------------------
// The service the screens use
// ---------------------------------------------------------------------------------------------

let current: { key: string; api: CloudDecksApi } | null = null;

/** Whether this is a development build (`vite dev`, a development-mode build, or a test run). */
const DEV_STAND_IN = import.meta.env.DEV || import.meta.env.MODE === 'development';

/**
 * The deck service for the signed-in user: the Supabase backend, or the local stand-in for the
 * simulated dev sign-in (there is no server to talk to then).
 */
export async function getCloudDecksApi(): Promise<CloudDecksApi> {
  const userId = (await StorageManager.getCurrentUserId()) || 'local-user-1';
  // The simulated sign-in only exists in development builds (see isDevAuthMock); the guard below
  // lets the bundler drop the sample-deck stand-in from staging and production builds.
  const dev = DEV_STAND_IN && isDevMockUserId(userId);
  const key = `${dev ? 'dev' : 'cloud'}:${userId}`;
  if (current?.key === key) return current.api;
  const backend = dev
    ? (await import('./devCloudDecks.js')).createDevBackend(userId)
    : (await import('./cloudDecksBackend.js')).createSupabaseBackend();
  const api = createCloudDecks({ userId, backend, mode: dev ? 'dev' : 'cloud' });
  current = { key, api };
  return api;
}

/** Test helper: forgets the cached service. */
export function resetCloudDecksApi(): void {
  current = null;
}
